import { createApiResponse } from '../models';
import {
  createAddToWatchlistRequest,
  createWatchlistItemWithCompany,
} from '../models/watchlistModels';
import {
  buildTenantRiskAnalysisRequest,
  mapTenantRiskAnalysisResult,
} from '../utils/tenantRiskAnalysisModel';
import { createUserApiClient } from './authenticatedApiClient';
import { getStoredUserId } from './authSession';
import {
  normalizeNodeRowsFromApi,
  serializeNodeRowsForApi,
} from '../utils/tenantNodeRowAdapter';
import { extractNodeListFromApiPayload } from '../utils/tenantNodeApiAdapter';
import {
  mapGraphContextCompanyRoutes,
  mapGraphContextToNodesByKey,
} from '../utils/tenantGraphContextAdapter';
import {
  clearEtagEntry,
  etagKeyForUser,
  getEtagEntry,
  setEtagEntry,
} from './etagStore';
import {
  clearGraphContextCache,
  getGraphContextCache,
  setGraphContextCache,
} from './graphContextCache';

const TENANT_GRAPH_CONTEXT_ETAG_RESOURCE = 'tenant-graph-context';

const TENANT_NODE_PATHS = {
  facilities: 'facilities',
  products: 'products',
  logistics: 'logistics',
  geographies: 'geographies',
  commodities: 'commodities',
  supplierFacilities: 'supplier-facilities',
  graphRelationships: 'graph-relationships',
};

const createTenantClient = (userId) => createUserApiClient(userId, 'tenant-company');

const withTenantClient = async (userId, requestHandler) => {
  const uid = userId || getStoredUserId();
  if (!uid) {
    return createApiResponse({ success: false, message: 'User not authenticated' });
  }
  try {
    const client = createTenantClient(uid);
    return await requestHandler(client, uid);
  } catch (error) {
    return createApiResponse({
      success: false,
      message: error.response?.data?.message || error.message || 'Tenant company request failed',
    });
  }
};

const invalidateGraphContextCache = (userId) => {
  clearEtagEntry(etagKeyForUser(TENANT_GRAPH_CONTEXT_ETAG_RESOURCE, userId));
  clearGraphContextCache(userId);
};

export const tenantCompanyService = {
  TENANT_NODE_PATHS,

  getTenantCompany(userId) {
    return withTenantClient(userId, async (client) => {
      try {
        const response = await client.get('/');
        return createApiResponse(response.data);
      } catch (error) {
        if (error.response?.status === 404) {
          // Tenant profile is optional for first-time users.
          return createApiResponse({
            success: true,
            data: null,
            message: 'Tenant company profile not found',
          });
        }
        throw error;
      }
    });
  },

  upsertTenantCompany(userId, payload) {
    return withTenantClient(userId, async (client, uid) => {
      const response = await client.put('/', payload);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success) invalidateGraphContextCache(uid);
      return apiResponse;
    });
  },

  deleteTenantCompany(userId) {
    return withTenantClient(userId, async (client) => {
      const response = await client.delete('/');
      return createApiResponse(response.data);
    });
  },

  getGraphContext(userId, options = {}) {
    return withTenantClient(userId, async (client, uid) => {
      const fetchGraphContext = async ({ useEtag = true, retrying = false } = {}) => {
        const etagKey = etagKeyForUser(TENANT_GRAPH_CONTEXT_ETAG_RESOURCE, uid);
        const stored = options.etag != null ? { etag: options.etag } : getEtagEntry(etagKey);

        const params = {};
        if (options.selectedSupplierIds?.length) {
          params.selectedSupplierIds = options.selectedSupplierIds.join(',');
        }
        if (options.includeWatchlistSuppliers === false) {
          params.includeWatchlistSuppliers = 'false';
        }
        if (options.view) params.view = options.view;

        const headers = {};
        if (useEtag && stored?.etag) headers['If-None-Match'] = stored.etag;

        const response = await client.get('/graph-context', {
          params,
          headers,
          validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
        });

        const responseEtag = response.headers?.etag ?? stored?.etag ?? '';

        if (response.status === 304) {
          const cached = getGraphContextCache(uid);
          if (cached?.nodesByKey) {
            return createApiResponse({
              success: true,
              notModified: true,
              etag: responseEtag,
              data: {
                nodesByKey: cached.nodesByKey ?? {},
                companyRoutes: cached.companyRoutes ?? {},
              },
            });
          }
          if (!retrying) {
            clearEtagEntry(etagKey);
            return fetchGraphContext({ useEtag: false, retrying: true });
          }
          return createApiResponse({
            success: false,
            message: 'Graph context not modified but no cached payload available',
          });
        }

        const apiResponse = createApiResponse(response.data);
        const payload = apiResponse.data ?? response.data?.data ?? response.data;
        if (!apiResponse.success && response.data?.success === false) {
          return apiResponse;
        }

        setEtagEntry(etagKey, { etag: responseEtag });

        const nodesByKey = mapGraphContextToNodesByKey(payload);
        const companyRoutes = mapGraphContextCompanyRoutes(payload);

        setGraphContextCache(uid, { nodesByKey, companyRoutes, etag: responseEtag });

        return createApiResponse({
          success: true,
          data: {
            nodesByKey,
            companyRoutes,
            raw: payload,
          },
          etag: responseEtag,
        });
      };

      try {
        return await fetchGraphContext();
      } catch (error) {
        if (error.response?.status === 404) {
          return createApiResponse({
            success: false,
            message: 'Graph context endpoint not available',
          });
        }
        throw error;
      }
    });
  },

  invalidateGraphContextCache(userId) {
    const uid = userId || getStoredUserId();
    if (uid) invalidateGraphContextCache(uid);
  },

  getTenantNodes(userId, nodeKey) {
    const nodePath = TENANT_NODE_PATHS[nodeKey];
    if (!nodePath) {
      return Promise.resolve(createApiResponse({ success: false, message: 'Unsupported tenant node type' }));
    }
    return withTenantClient(userId, async (client) => {
      const response = await client.get(`/${nodePath}`);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success) {
        const rows = extractNodeListFromApiPayload(apiResponse.data);
        apiResponse.data = normalizeNodeRowsFromApi(nodeKey, rows);
      }
      return apiResponse;
    });
  },

  upsertTenantNodes(userId, nodeKey, rows) {
    const nodePath = TENANT_NODE_PATHS[nodeKey];
    if (!nodePath) {
      return Promise.resolve(createApiResponse({ success: false, message: 'Unsupported tenant node type' }));
    }
    return withTenantClient(userId, async (client, uid) => {
      const payload = serializeNodeRowsForApi(nodeKey, rows);
      const response = await client.put(`/${nodePath}`, payload);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success) {
        const rows = extractNodeListFromApiPayload(apiResponse.data);
        apiResponse.data = normalizeNodeRowsFromApi(nodeKey, rows);
        invalidateGraphContextCache(uid);
      }
      return apiResponse;
    });
  },

  getTenantSuppliers(userId) {
    return withTenantClient(userId, async (client) => {
      const response = await client.get('/suppliers');
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && Array.isArray(apiResponse.data)) {
        apiResponse.data = apiResponse.data.map((item) => createWatchlistItemWithCompany(item));
      }
      return apiResponse;
    });
  },

  addTenantSupplier(userId, requestData) {
    return withTenantClient(userId, async (client) => {
      const request = createAddToWatchlistRequest(requestData);
      const validationErrors = request.validate();
      if (validationErrors.length > 0) {
        return createApiResponse({
          success: false,
          message: `Validation errors: ${validationErrors.join(', ')}`,
        });
      }
      const response = await client.post('/suppliers', request.toJSON());
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createWatchlistItemWithCompany({
          watchlist_item: apiResponse.data,
          watchlistItem: apiResponse.data,
        });
      }
      return apiResponse;
    });
  },

  addTenantSuppliersBulk(userId, requests) {
    return withTenantClient(userId, async (client) => {
      const payload = (requests || []).map((item) => createAddToWatchlistRequest(item).toJSON());
      const response = await client.post('/suppliers/bulk', payload);
      return createApiResponse(response.data);
    });
  },

  removeTenantSupplier(userId, ticker) {
    const normalizedTicker = String(ticker || '').trim().toUpperCase();
    if (!normalizedTicker) {
      return Promise.resolve(createApiResponse({ success: false, message: 'Supplier ticker is required' }));
    }
    return withTenantClient(userId, async (client) => {
      const response = await client.delete(`/suppliers/${encodeURIComponent(normalizedTicker)}`);
      return createApiResponse(response.data);
    });
  },

  analyzeTenantRisk(userId, options = {}) {
    const uid = userId || getStoredUserId();
    if (!uid) {
      return Promise.resolve(createApiResponse({ success: false, message: 'User not authenticated' }));
    }
    const body = buildTenantRiskAnalysisRequest(uid, options);
    return withTenantClient(uid, async (client) => {
      const response = await client.post('/risk-analysis/analyze', body);
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = mapTenantRiskAnalysisResult(apiResponse.data);
      }
      return apiResponse;
    });
  },

  getLatestTenantRiskAnalysis(userId) {
    return withTenantClient(userId, async (client) => {
      try {
        const response = await client.get('/risk-analysis/latest');
        const apiResponse = createApiResponse(response.data);
        if (apiResponse.success && apiResponse.data) {
          apiResponse.data = mapTenantRiskAnalysisResult(apiResponse.data);
        }
        return apiResponse;
      } catch (error) {
        const status = error.response?.status;
        // No saved analysis yet (404) or backend read failure (500) — treat as empty state.
        if (status === 404 || status === 500) {
          return createApiResponse({
            success: true,
            data: null,
            message: 'No tenant risk analysis found',
          });
        }
        return createApiResponse({
          success: false,
          message:
            error.response?.data?.message ||
            error.message ||
            'Failed to load tenant risk analysis',
        });
      }
    });
  },
};

